import React, { useEffect, useState } from "react";
import { Table } from "antd";
import Breadcrumbs from "../../../components/Breadcrumbs";
import { useLazyGetGuarantorRequestsQuery } from "../../../services/employee.service";
import { formatNumber } from "../../../utils";
import dayjs from "dayjs";
import { useSelector } from "react-redux";

const GuarantorRequests = () => {
  const { user } = useSelector((state) => state.user);
  const [pagination, setPagination] = useState({});
  const [results, setResult] = useState([]);
  const [selectedLoan, setSelectedLoan] = useState(null);

  const [getGuarantorRequests, { data, isFetching }] = useLazyGetGuarantorRequestsQuery();

  const handleViewDetails = (loan) => {
    setSelectedLoan(loan);
  };

  useEffect(() => {
    getGuarantorRequests({ page: 1, limit: 10 });
  }, []);

  useEffect(() => {
    if (data) {
      setResult(data?.data?.result || []);
      setPagination(data?.data?.meta || {});
    }
  }, [data]);

  // Calculate statistics from guarantor requests
  const stats = {
    total: results.length,
    pending: results.filter((l) => l.status === "Pending" || l.status === "New").length,
    approved: results.filter((l) => l.status === "Approved").length,
    rejected: results.filter((l) => l.status === "Rejected").length,
  };

  const statsData = [
    {
      title: "Total Requests",
      value: stats.total,
      icon: "la la-file-text",
      color: "primary",
    },
    {
      title: "Pending",
      value: stats.pending,
      icon: "la la-clock-o",
      color: "warning",
    },
    {
      title: "Approved",
      value: stats.approved,
      icon: "la la-check-circle",
      color: "success",
    },
    {
      title: "Rejected",
      value: stats.rejected,
      icon: "la la-times-circle",
      color: "danger",
    },
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case "New":
        return "purple";
      case "Pending":
        return "info";
      case "Approved":
        return "success";
      case "Disbursed":
        return "primary";
      case "Repaying":
        return "warning";
      case "Completed":
        return "success";
      case "Rejected":
        return "danger";
      default:
        return "secondary";
    }
  };

  const columns = [
    {
      title: "#",
      dataIndex: "id",
      render: (t, r, i) => i + 1,
      width: 50,
    },
    {
      title: "Employee",
      dataIndex: "employee",
      render: (employee) => (
        <div className="table-avatar">
          <a href="#" className="avatar avatar-xs">
            <img
              src={employee?.profile_pic || "/assets/img/profiles/avatar-default.jpg"}
              alt={`${employee?.first_name} ${employee?.last_name}`}
            />
          </a>
          <a href="#">
            {employee?.first_name} {employee?.last_name}
            <br />
            <span className="text-muted small">{employee?.designation?.name || "N/A"}</span>
          </a>
        </div>
      ),
      sorter: (a, b) =>
        `${a.employee?.first_name} ${a.employee?.last_name}`.localeCompare(
          `${b.employee?.first_name} ${b.employee?.last_name}`
        ),
    },
    {
      title: "Department",
      dataIndex: "employee",
      render: (employee) => <span>{employee?.department?.name || "N/A"}</span>,
      sorter: (a, b) =>
        (a.employee?.department?.name || "").localeCompare(b.employee?.department?.name || ""),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      render: (text) => <span className="fw-bold">₦{formatNumber(text)}</span>,
      sorter: (a, b) => parseFloat(a.amount) - parseFloat(b.amount),
    },
    {
      title: "Tenure",
      dataIndex: "tenure",
      render: (text) => <span>{text} months</span>,
      sorter: (a, b) => a.tenure - b.tenure,
    },
    {
      title: "Monthly Repayment",
      dataIndex: "monthly_repayment",
      render: (text) => <span>₦{formatNumber(text)}</span>,
      sorter: (a, b) => parseFloat(a.monthly_repayment) - parseFloat(b.monthly_repayment),
    },
    {
      title: "Request Date",
      dataIndex: "created_at",
      render: (text) => <span>{text ? dayjs(text).format("DD MMM YYYY") : "N/A"}</span>,
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (text) => (
        <span className={`badge bg-inverse-${getStatusColor(text)}`}>
          {text}
        </span>
      ),
      sorter: (a, b) => a.status.localeCompare(b.status),
    },
    {
      title: "Reason",
      dataIndex: "reason",
      render: (text) => (
        <span className="text-truncate" style={{ maxWidth: "200px", display: "inline-block" }}>
          {text || "N/A"}
        </span>
      ),
    },
    {
      title: "Other Guarantors",
      dataIndex: "guarantors",
      render: (guarantors, record) => {
        // Filter out current user from guarantors list
        const otherGuarantors = (guarantors || []).filter(g => g.id !== user?.id);
        if (otherGuarantors.length === 0) {
          return <span className="text-muted">None</span>;
        }
        return (
          <div>
            {otherGuarantors.slice(0, 2).map((g, idx) => (
              <div key={idx} className="text-xs">
                {g.first_name} {g.last_name}
              </div>
            ))}
            {otherGuarantors.length > 2 && (
              <span className="text-muted small">
                +{otherGuarantors.length - 2} more
              </span>
            )}
          </div>
        );
      },
    },
    {
      title: "Actions",
      dataIndex: "actions",
      render: (_, record) => (
        <div className="dropdown dropdown-action">
          <a
            href="#"
            className="action-icon dropdown-toggle"
            data-bs-toggle="dropdown"
            aria-expanded="false"
            onClick={(e) => e.preventDefault()}
          >
            <i className="material-icons">more_vert</i>
          </a>
          <div className="dropdown-menu dropdown-menu-right">
            <a
              className="dropdown-item"
              href="#"
              data-bs-toggle="modal"
              data-bs-target="#view_loan_modal"
              onClick={(e) => {
                e.preventDefault();
                handleViewDetails(record);
              }}
            >
              <i className="fa fa-eye m-r-5" /> View Details
            </a>
          </div>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="page-wrapper">
        <div className="content container-fluid">
          <Breadcrumbs
            maintitle="Guarantor Requests"
            title="Dashboard"
            subtitle="Guarantor Requests"
          />

          {/* Statistics */}
          <div className="row">
            {statsData.map((stat, index) => (
              <div className="col-md-6 col-sm-6 col-lg-6 col-xl-3" key={index}>
                <div className="card dash-widget">
                  <div className="card-body">
                    <span className="dash-widget-icon">
                      <i className={stat.icon} />
                    </span>
                    <div className="dash-widget-info">
                      <h3>{stat.value}</h3>
                      <span>{stat.title}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Information Card */}
          <div className="row">
            <div className="col-md-12">
              <div className="card">
                <div className="card-body">
                  <div className="alert alert-info mb-0">
                    <i className="fa fa-info-circle me-2"></i>
                    These are loan requests where you have been listed as a guarantor.
                    Review the details carefully before the employee proceeds with their loan application.
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Loan Table */}
          <div className="row">
            <div className="col-md-12">
              <div className="table-responsive">
                <Table
                  className="table-striped"
                  columns={columns}
                  loading={isFetching}
                  dataSource={results}
                  rowKey={(record) => record.id}
                  pagination={{
                    defaultPageSize: 10,
                    total: pagination?.total || results.length,
                    showTotal: (total, range) =>
                      `${range[0]}-${range[1]} of ${total} items`,
                    onChange: (page, pageSize) => {
                      getGuarantorRequests({ page, limit: pageSize });
                    },
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* View Loan Details Modal */}
      <div id="view_loan_modal" className="modal custom-modal fade" role="dialog">
        <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Loan Request Details</h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <span aria-hidden="true">×</span>
              </button>
            </div>
            <div className="modal-body">
              {selectedLoan && (
                <div>
                  {/* Employee Information */}
                  <div className="row mb-4">
                    <div className="col-md-12">
                      <h6 className="text-primary mb-3">Employee Information</h6>
                      <div className="row">
                        <div className="col-md-6">
                          <p className="mb-2">
                            <strong>Name:</strong> {selectedLoan.employee?.first_name} {selectedLoan.employee?.last_name}
                          </p>
                          <p className="mb-2">
                            <strong>Employee ID:</strong> {selectedLoan.employee?.emp_id || 'N/A'}
                          </p>
                        </div>
                        <div className="col-md-6">
                          <p className="mb-2">
                            <strong>Department:</strong> {selectedLoan.employee?.department?.name || 'N/A'}
                          </p>
                          <p className="mb-2">
                            <strong>Designation:</strong> {selectedLoan.employee?.designation?.name || 'N/A'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Loan Details */}
                  <div className="row mb-4">
                    <div className="col-md-12">
                      <h6 className="text-primary mb-3">Loan Details</h6>
                      <div className="row">
                        <div className="col-md-6">
                          <p className="mb-2">
                            <strong>Amount:</strong> ₦{formatNumber(selectedLoan.amount)}
                          </p>
                          <p className="mb-2">
                            <strong>Tenure:</strong> {selectedLoan.tenure} months
                          </p>
                          <p className="mb-2">
                            <strong>Monthly Repayment:</strong> ₦{formatNumber(selectedLoan.monthly_repayment)}
                          </p>
                        </div>
                        <div className="col-md-6">
                          <p className="mb-2">
                            <strong>Start Date:</strong> {selectedLoan.start_date ? dayjs(selectedLoan.start_date).format('DD MMM YYYY') : 'N/A'}
                          </p>
                          <p className="mb-2">
                            <strong>Request Date:</strong> {dayjs(selectedLoan.created_at).format('DD MMM YYYY')}
                          </p>
                          <p className="mb-2">
                            <strong>Status:</strong>{' '}
                            <span className={`badge bg-inverse-${getStatusColor(selectedLoan.status)}`}>
                              {selectedLoan.status}
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Reason */}
                  <div className="row mb-4">
                    <div className="col-md-12">
                      <h6 className="text-primary mb-3">Reason for Loan</h6>
                      <p className="mb-0">{selectedLoan.reason || 'N/A'}</p>
                    </div>
                  </div>

                  {/* All Guarantors */}
                  <div className="row mb-3">
                    <div className="col-md-12">
                      <h6 className="text-primary mb-3">All Guarantors</h6>
                      <div className="table-responsive">
                        <table className="table table-bordered">
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>Name</th>
                              <th>Department</th>
                              <th>Designation</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(selectedLoan.guarantors || []).map((guarantor, idx) => (
                              <tr key={idx} className={guarantor.id === user?.id ? 'table-active' : ''}>
                                <td>{idx + 1}</td>
                                <td>
                                  {guarantor.first_name} {guarantor.last_name}
                                  {guarantor.id === user?.id && (
                                    <span className="badge bg-primary ms-2">You</span>
                                  )}
                                </td>
                                <td>{guarantor.department?.name || 'N/A'}</td>
                                <td>{guarantor.designation?.name || 'N/A'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  {/* Remarks if any */}
                  {selectedLoan.approval_remarks && (
                    <div className="row">
                      <div className="col-md-12">
                        <h6 className="text-primary mb-3">Approval Remarks</h6>
                        <p className="mb-0">{selectedLoan.approval_remarks}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default GuarantorRequests;
